import { msg } from '@gonfalon/messages/msg';
import { Button, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import type { ParsedToken } from '../../../types/QueryBuilder';
import { useFilterBarContext } from '../FilterBarContext';
import { useQueryBuilderGridItem } from '../hooks/useQueryBuilderGridItem';

import { LogicOperatorInput, useLogicOperatorEditing } from './LogicOperatorInput';

import styles from './LogicBoolToken.module.css';

type LogicNotParsedToken = Extract<ParsedToken, { type: 'LOGIC_NOT' }>;

interface LogicNotTokenProps {
  tokenKey: string;
  token: LogicNotParsedToken;
  onRemove: () => void;
  removable?: boolean;
}

/** Unary NOT is a standalone token so it can precede any filter or expression. */
export function LogicNotToken({ tokenKey, token, onRemove, removable = true }: LogicNotTokenProps) {
  const { readonly, gridState } = useFilterBarContext();
  const { containerProps, cellProps } = useQueryBuilderGridItem(tokenKey, gridState);
  const editing = useLogicOperatorEditing(tokenKey, token);
  const label = msg('queryBuilder.logicOperator.label', { operator: token.value });

  function renderOperator() {
    if (readonly) {
      return (
        <span data-part="operator" className={styles.label} aria-label={label}>
          {token.value}
        </span>
      );
    }
    if (editing.isEditing) {
      return (
        <LogicOperatorInput
          tokenKey={tokenKey}
          operator={token.value}
          value={editing.value}
          onInput={editing.handleInput}
          onStop={editing.stopEditing}
        />
      );
    }
    return (
      <Button data-part="operator" className={styles.cell} aria-label={label} onPress={editing.startEditing}>
        {token.value}
      </Button>
    );
  }

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper exists only to stop click propagation; interactive controls live inside
    <div className={styles.container} onClick={(event) => event.stopPropagation()}>
      <div {...containerProps} className={styles.logicBoolToken}>
        <div {...cellProps}>
          <span className={styles.editableCell}>{renderOperator()}</span>
        </div>
        {removable && !readonly && (
          <div {...cellProps}>
            <TooltipTrigger>
              <IconButton
                className={styles.removeButton}
                icon="cancel"
                variant="minimal"
                size="small"
                aria-label={msg('queryBuilder.logicOperator.removeLabel')}
                onPress={onRemove}
              />
              <Tooltip>{msg('queryBuilder.logicOperator.removeTooltip')}</Tooltip>
            </TooltipTrigger>
          </div>
        )}
      </div>
    </div>
  );
}

LogicNotToken.displayName = 'LogicNotToken';
